import { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Package, MapPin, Image, MessageSquare, Inbox,
  CalendarCheck, FileText, Settings, LogOut, Menu, X, Sparkles, ExternalLink, UserCog, Star, Wallet,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND_LOGO } from "@/components/SEO";
import { useSettings } from "@/context/SettingsContext";

const nav = [
  { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, end: true, always: true },
  { to: "/admin/enquiries", label: "Enquiries", Icon: Inbox, perm: "enquiries" },
  { to: "/admin/bookings", label: "Bookings", Icon: CalendarCheck, perm: "bookings" },
  { to: "/admin/payments", label: "Payments", Icon: Wallet, perm: "bookings" },
  { to: "/admin/tours", label: "Tours", Icon: Package, perm: "tours" },
  { to: "/admin/destinations", label: "Destinations", Icon: MapPin, perm: "destinations" },
  { to: "/admin/gallery", label: "Gallery", Icon: Image, perm: "gallery" },
  { to: "/admin/testimonials", label: "Testimonials", Icon: MessageSquare, perm: "testimonials" },
  { to: "/admin/reviews", label: "Reviews", Icon: Star, perm: "testimonials" },
  { to: "/admin/blog", label: "AI Blog", Icon: Sparkles, perm: "blog" },
  { to: "/admin/users", label: "Manage Users", Icon: UserCog, perm: "users" },
  { to: "/admin/settings", label: "Settings", Icon: Settings, perm: "settings" },
];

export default function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const settings = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const visibleNav = user?.role === "staff" ? nav.filter((n) => n.always || (user.permissions || []).includes(n.perm)) : nav;

  const SidebarContent = () => (
    <>
      <Link to="/" className="mb-6 flex items-center justify-center rounded-2xl bg-white p-3"><img src={settings?.logo || BRAND_LOGO} alt="Happy Holidays Wale" className="h-9 w-auto" /></Link>
      <nav className="flex-1 space-y-1">
        {visibleNav.map(({ to, label, Icon, end }) => (
          <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${isActive ? "brand-gradient text-white" : "text-white/70 hover:bg-white/10"}`}>
            <Icon className="h-5 w-5" /> {label}
          </NavLink>
        ))}
      </nav>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-0 hidden h-full w-64 flex-col p-4 lg:flex" style={{ backgroundColor: "#002244" }}><SidebarContent /></aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col p-4" style={{ backgroundColor: "#002244" }}><SidebarContent /></aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-gray-200 bg-white/80 px-4 py-4 backdrop-blur-xl md:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu className="h-6 w-6 text-navy" /></button>
            <h1 className="font-display text-xl font-bold text-navy md:text-2xl">{title}</h1>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/" target="_blank" data-testid="view-website-btn" className="flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 font-semibold text-navy hover:bg-navy/5"><ExternalLink className="h-4 w-4" /> View Website</Link>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="flex items-center gap-1.5 rounded-full border border-brandred/30 px-4 py-2 font-semibold text-brandred hover:bg-red-50"><LogOut className="h-4 w-4" /> Logout</button>
            <span className="hidden text-gray-500 sm:inline">{user?.email}</span>
            <span className="flex h-9 w-9 items-center justify-center rounded-full brand-gradient font-bold text-white">{user?.name?.[0] || "A"}</span>
          </div>
        </header>
        <main className="p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}
